import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'exit_popup_shown_pnl';

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 min countdown
  const shownRef = useRef(false);

  // Exit intent via mouseleave at top of viewport
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !shownRef.current) {
        shownRef.current = true;
        sessionStorage.setItem(STORAGE_KEY, '1');
        setTimeout(() => setVisible(true), 80);
      }
    };

    // Delay to avoid firing on page load
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 4000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [visible]);

  const close = () => {
    setDismissed(true);
    setTimeout(() => setVisible(false), 300);
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center px-4 transition-all duration-300 ${
        dismissed ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-charcoal-950/85 backdrop-blur-sm"
        onClick={close}
      ></div>

      {/* Modal */}
      <div
        className={`relative w-full max-w-lg bg-white rounded-3xl overflow-hidden transition-all duration-300 ${
          dismissed ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        {/* Topo colorido */}
        <div className="bg-gradient-to-r from-charcoal-900 via-charcoal-800 to-charcoal-900 px-8 pt-10 pb-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(201,164,64,0.12)_0%,_transparent_60%)] pointer-events-none"></div>

          <button
            onClick={close}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white/70 text-sm"></i>
          </button>

          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gold-500/15 border border-gold-500/30 mx-auto mb-5">
            <i className="ri-gift-2-line text-3xl text-gold-400"></i>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-500/15 border border-gold-500/25 rounded-full text-gold-300 text-xs font-semibold tracking-widest uppercase mb-4">
            <i className="ri-time-line animate-pulse"></i>
            Oferta por tempo limitado
          </div>

          <h2 className="font-display text-3xl font-bold text-white mb-2 leading-tight">
            Espera! Antes de ir…
          </h2>
          <p className="text-white/60 text-sm font-light leading-relaxed">
            Você está prestes a deixar técnicas que podem transformar sua vida na mesa.
          </p>
        </div>

        {/* Corpo */}
        <div className="px-8 py-6">
          {/* Oferta */}
          <div className="bg-cream-50 border border-gold-200 rounded-2xl p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-charcoal-800">Pacote 6 Técnicas Completas</span>
              <div className="flex items-center gap-2">
                <span className="text-charcoal-400 text-sm line-through">R$ 108</span>
                <span className="font-display text-2xl font-bold text-gold-600">R$ 47</span>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              {[
                'Protocolos completos de cada técnica',
                'Áudios guiados de meditação',
                'Workbooks em PDF para download',
                'Acesso vitalício sem mensalidade',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-gold-500 text-sm mt-0.5 flex-shrink-0"></i>
                  <span className="text-xs text-charcoal-600">{item}</span>
                </div>
              ))}
            </div>

            {/* Countdown */}
            <div className="flex items-center justify-between bg-charcoal-900 rounded-xl px-4 py-2.5">
              <span className="text-xs text-white/60 font-medium">Oferta expira em</span>
              <span className="font-mono text-lg font-bold text-gold-400">{formatTime(timeLeft)}</span>
            </div>
          </div>

          {/* Garantia */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 flex-shrink-0">
              <i className="ri-shield-check-line text-emerald-600 text-lg"></i>
            </div>
            <p className="text-xs text-charcoal-500 leading-relaxed">
              <strong className="text-charcoal-700">Garantia de 7 dias.</strong> Se não sentir transformação, devolvemos 100% do valor — sem perguntas.
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-3">
            <Link
              to="/planos#exercicios-pnl"
              onClick={close}
              className="w-full py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-xl font-bold text-sm tracking-wide hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap text-center cursor-pointer"
            >
              Quero o Pacote por R$ 47
              <span className="ml-2 text-xs font-normal opacity-70">(economia de 56%)</span>
            </Link>
            <button
              onClick={close}
              className="w-full py-3 text-charcoal-400 text-xs font-medium hover:text-charcoal-600 transition-colors cursor-pointer"
            >
              Não, prefiro continuar no plano gratuito
            </button>
          </div>
        </div>

        {/* Prova social */}
        <div className="border-t border-charcoal-100 px-8 py-4 bg-cream-50 flex items-center justify-center gap-6">
          {[
            { value: '10k+', label: 'vidas transformadas' },
            { value: '4.9', label: 'avaliação média' },
            { value: '7 dias', label: 'garantia total' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-lg font-bold text-charcoal-900">{s.value}</div>
              <div className="text-xs text-charcoal-400">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
